import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPLN, formatDate } from "../lib/api";
import { Folders, ArrowUpRight, Phone, User } from "lucide-react";
import { Button } from "../components/ui/button";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/orders", { params: { status: "active" } });
      setOrders(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="orders-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2">
            Aktywne zlecenia
          </div>
          <h1 className="heading text-3xl sm:text-4xl font-semibold tracking-tight">
            Zlecenia w toku
          </h1>
        </div>
        <Link to="/zlecenia/nowe" data-testid="goto-new-order">
          <Button className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            Nowe zlecenie
          </Button>
        </Link>
      </header>

      {loading ? (
        <div className="surface p-10 text-center text-[var(--text-muted)]">Wczytywanie...</div>
      ) : orders.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="orders-list">
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} />
          ))}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order }) {
  const profit = Number(order.profit || 0);
  return (
    <Link
      to={`/zlecenia/${order.id}`}
      className="tab-action surface p-5 flex flex-col gap-3"
      data-testid={`order-card-${order.id}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="heading text-lg font-semibold">{order.name}</div>
          <div className="text-xs text-[var(--text-muted)] mt-1">
            Utworzone: {formatDate(order.created_at)}
          </div>
        </div>
        <ArrowUpRight className="text-[var(--text-muted)]" size={18} />
      </div>
      <div className="flex flex-wrap gap-3 text-sm text-[var(--text-muted)]">
        {order.client_name && (
          <span className="inline-flex items-center gap-1">
            <User size={14} /> {order.client_name}
          </span>
        )}
        {order.client_phone && (
          <span className="inline-flex items-center gap-1">
            <Phone size={14} /> {order.client_phone}
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[var(--border)] text-sm">
        <Cell label="Koszty" value={formatPLN(order.total_costs)} />
        <Cell label="Cena" value={formatPLN(order.sale_price)} />
        <Cell
          label="Zarobek"
          value={formatPLN(profit)}
          accent={profit >= 0 ? "var(--success)" : "var(--danger)"}
        />
      </div>
    </Link>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div>
      <div className="text-xs text-[var(--text-muted)]">{label}</div>
      <div
        className="font-semibold tabular-nums"
        style={accent ? { color: accent } : {}}
      >
        {value}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="surface p-12 text-center" data-testid="orders-empty">
      <div className="w-14 h-14 rounded-full bg-[var(--surface-2)] mx-auto mb-4 flex items-center justify-center">
        <Folders className="text-[var(--primary)]" size={22} />
      </div>
      <div className="heading text-lg font-semibold">Brak aktywnych zleceń</div>
      <p className="text-sm text-[var(--text-muted)] mt-1">
        Załóż nowe zlecenie aby rozpocząć śledzenie kosztów.
      </p>
      <Link to="/zlecenia/nowe" className="inline-block mt-4">
        <Button className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
          Nowe zlecenie
        </Button>
      </Link>
    </div>
  );
}
